import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

const API = process.env.REACT_APP_API_URL;

export default function SuperAdminFacturas() {
  const { empresaId }           = useParams();
  const [empresa, setEmpresa]   = useState(null);
  const [facturas, setFacturas] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');
  const [downloading, setDownloading] = useState(null);
  const navigate                = useNavigate();
  const token                   = localStorage.getItem('sa_token');

  useEffect(() => {
    if (!token) { navigate('/superadmin/login'); return; }

    const headers = { Authorization: `Bearer ${token}` };

    Promise.all([
      fetch(`${API}/superadmin/empresas`, { headers }).then(r => r.json()),
      fetch(`${API}/superadmin/empresas/${empresaId}/facturas`, { headers }).then(r => {
        if (r.status === 401) { navigate('/superadmin/login'); throw new Error(); }
        return r.json();
      }),
    ])
      .then(([empresas, invs]) => {
        setEmpresa(empresas.find(e => String(e.id) === String(empresaId)) || null);
        setFacturas(invs);
      })
      .catch(() => setError('Error al cargar los datos.'))
      .finally(() => setLoading(false));
  }, [empresaId]); // eslint-disable-line

  const downloadPdf = async (invoiceId, number) => {
    setDownloading(invoiceId);
    try {
      const res = await fetch(
        `${API}/superadmin/empresas/${empresaId}/facturas/${invoiceId}/download`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) { alert('No se pudo descargar la factura.'); return; }
      const blob = await res.blob();
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href     = url;
      a.download = `factura-${number}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      alert('Error al descargar.');
    } finally {
      setDownloading(null);
    }
  };

  const logout = () => { localStorage.removeItem('sa_token'); navigate('/superadmin/login'); };

  return (
    <div style={styles.bg}>
      <div style={styles.topbar}>
        <div><span style={styles.brand}>TURNORA</span><span style={styles.badge}>SUPERADMIN</span></div>
        <button onClick={logout} style={styles.logoutBtn}>Cerrar sesión</button>
      </div>

      <div style={styles.container}>
        <button onClick={() => navigate('/superadmin')} style={styles.backBtn}>← Volver</button>

        <div style={styles.card}>
          <h2 style={styles.h2}>
            Facturas · {empresa ? empresa.nombre_fiscal : `Empresa #${empresaId}`}
          </h2>
          {empresa && (
            <p style={styles.meta}>
              CIF: {empresa.cif || '—'} &nbsp;·&nbsp; {empresa.email || ''}
            </p>
          )}

          {loading && <p style={styles.muted}>Cargando…</p>}
          {error   && <p style={styles.errorMsg}>{error}</p>}
          {!loading && !error && facturas.length === 0 && (
            <p style={styles.muted}>Esta empresa no tiene facturas en Stripe.</p>
          )}

          {facturas.length > 0 && (
            <table style={styles.table}>
              <thead>
                <tr>
                  {['Número', 'Fecha', 'Importe', 'Estado', ''].map(h => (
                    <th key={h} style={styles.th}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {facturas.map(f => (
                  <tr key={f.id}>
                    <td style={styles.td}><strong>{f.number}</strong></td>
                    <td style={styles.td}>{f.date}</td>
                    <td style={styles.td}>{f.total}</td>
                    <td style={styles.td}>
                      <span style={f.paid ? styles.pillGreen : styles.pillRed}>
                        {f.paid ? 'Pagada' : 'Pendiente'}
                      </span>
                    </td>
                    <td style={styles.td}>
                      <button
                        style={styles.btnPrimary}
                        disabled={downloading === f.id}
                        onClick={() => downloadPdf(f.id, f.number)}
                      >
                        {downloading === f.id ? 'Generando…' : 'Descargar PDF'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

const styles = {
  bg:         { minHeight: '100vh', background: '#f1f5f9', fontFamily: "'Segoe UI', Arial, sans-serif", fontSize: 14 },
  topbar:     { background: '#0f172a', padding: '0 32px', height: 52, display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  brand:      { fontSize: 16, fontWeight: 700, color: '#FF7A00', letterSpacing: .5 },
  badge:      { fontSize: 11, background: '#FF7A00', color: '#fff', padding: '2px 8px', borderRadius: 4, marginLeft: 10 },
  logoutBtn:  { background: 'transparent', border: '1px solid #475569', color: '#94a3b8', padding: '4px 14px', borderRadius: 6, cursor: 'pointer', fontSize: 12 },
  container:  { maxWidth: 960, margin: '36px auto', padding: '0 20px' },
  backBtn:    { background: 'transparent', border: '1px solid #e2e8f0', color: '#475569', padding: '6px 16px', borderRadius: 6, cursor: 'pointer', fontSize: 12, marginBottom: 16 },
  card:       { background: '#fff', borderRadius: 10, boxShadow: '0 1px 4px rgba(0,0,0,.08)', padding: '28px 32px' },
  h2:         { fontSize: 17, fontWeight: 700, marginBottom: 6, color: '#0f172a' },
  meta:       { fontSize: 12, color: '#94a3b8', marginBottom: 20 },
  muted:      { color: '#94a3b8', fontSize: 13, padding: '20px 0', textAlign: 'center' },
  errorMsg:   { color: '#b91c1c', background: '#fee2e2', padding: '10px 16px', borderRadius: 6, fontSize: 13 },
  table:      { width: '100%', borderCollapse: 'collapse' },
  th:         { textAlign: 'left', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: .6, color: '#64748b', padding: '8px 12px', borderBottom: '2px solid #f1f5f9' },
  td:         { padding: '11px 12px', borderBottom: '1px solid #f8fafc', fontSize: 13, verticalAlign: 'middle' },
  btnPrimary: { background: '#FF7A00', color: '#fff', border: 'none', padding: '6px 16px', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer' },
  pillGreen:  { background: '#dcfce7', color: '#15803d', fontSize: 11, fontWeight: 700, padding: '2px 10px', borderRadius: 99 },
  pillRed:    { background: '#fee2e2', color: '#b91c1c', fontSize: 11, fontWeight: 700, padding: '2px 10px', borderRadius: 99 },
};
